import axios from 'axios';
import API_BASE_URL from './urlHelper';
import jwtUtils from '../utilities/jwtUtils';

const REFRESH_TOKEN_VALIDATION_TTL_MS = 2 * 60 * 1000;

let refreshTokenValidationCache = {
  key: null,
  valid: false,
  expiresAt: 0,
};

let refreshTokenValidationPromise = null;
let refreshAccessTokenPromise = null;

function getRefreshTokenContext() {
  const refreshToken = jwtUtils.getRefreshTokenFromCookie();
  const refreshTokenId = jwtUtils.getRefreshTokenIDFromCookie();
  const userID = jwtUtils.getUserID(refreshToken);

  return {
    refreshToken,
    refreshTokenId,
    userID,
    key: refreshTokenId && userID ? `${refreshTokenId}:${userID}` : null,
  };
}

function cacheRefreshTokenValidation(key, valid) {
  refreshTokenValidationCache = {
    key,
    valid,
    expiresAt: valid ? Date.now() + REFRESH_TOKEN_VALIDATION_TTL_MS : 0,
  };
}

function clearRefreshTokenValidationCache() {
  refreshTokenValidationCache = {
    key: null,
    valid: false,
    expiresAt: 0,
  };
}

async function validateRefreshTokenID({ force = false } = {}) {
  try {
    const { refreshTokenId, userID, key } = getRefreshTokenContext();
    
    if (!refreshTokenId || !userID || !key) {
      console.log('[Token] No se encontró ID de refresh token o ID de usuario');
      return false;
    }

    if (
      !force &&
      refreshTokenValidationCache.key === key &&
      refreshTokenValidationCache.expiresAt > Date.now()
    ) {
      return refreshTokenValidationCache.valid;
    }

    if (refreshTokenValidationPromise?.key === key) {
      return refreshTokenValidationPromise.promise;
    }
    
    const promise = axios
      .post(`${API_BASE_URL}/api/validate-refresh-token`, {
        refresh_token_id: refreshTokenId,
        userID,
      })
      .then((response) => {
        const isValid = Boolean(response.data.valid);

        if (isValid) {
          cacheRefreshTokenValidation(key, true);
        } else {
          clearRefreshTokenValidationCache();
        }

        return isValid;
      })
      .catch((error) => {
        console.error('[Token] Error al validar ID de refresh token:', error.message);
        clearRefreshTokenValidationCache();
        return false;
      })
      .finally(() => {
        if (refreshTokenValidationPromise?.key === key) {
          refreshTokenValidationPromise = null;
        }
      });

    refreshTokenValidationPromise = { key, promise };
    return promise;
  } catch (error) {
    console.error('[Token] Error al validar ID de refresh token:', error.message);
    clearRefreshTokenValidationCache();
    return false;
  }
}

async function refreshAccessToken() {
  if (refreshAccessTokenPromise) {
    return refreshAccessTokenPromise;
  }

  refreshAccessTokenPromise = (async () => {
    // console.log('[Token] Iniciando renovación del access token...');
    try {
      const { refreshToken, key } = getRefreshTokenContext();
      //console.log('[Token] Refresh token obtenido de cookies');
      
      const response = await axios.post(`${API_BASE_URL}/api/refresh`, { refresh_token: refreshToken });
      //console.log('[Token] Respuesta del servidor recibida');
      
      const newAccessToken = response.data.access_token;
      jwtUtils.setAccessTokenInCookie(newAccessToken);

      if (key) {
        cacheRefreshTokenValidation(key, true);
      }

      // console.log('[Token] Nuevo access token guardado en cookies');
      return newAccessToken;
    } catch (error) {
      //console.error('[Token] Error al refrescar el token:', error.message);
      //console.log('[Token] Ejecutando logout...');
      logout();
      throw error;
    } finally {
      refreshAccessTokenPromise = null;
    }
  })();

  return refreshAccessTokenPromise;
}

function isTokenExpired(token) {
  if (!token) {
    // console.log('[Token] No se encontró token');
    return true;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // console.log(`[Token] Expiración: ${new Date(payload.exp * 1000).toLocaleTimeString()}`);
    
    const expirado = payload.exp * 1000 < Date.now();
    // if (expirado) console.log('[Token] Token expirado');
    // else console.log('[Token] Token aún válido');
    
    return expirado;
  } catch (error) {
    // console.error('[Token] Error al decodificar token:', error.message);
    return true;
  }
}

async function verificarYRenovarToken() {
  // console.log('[Token] Verificando estado del token...');

  let access_token = jwtUtils.getAccessTokenFromCookie();

  if (!isTokenExpired(access_token)) {
    // console.log('[Token] Access token aún válido. No se requiere renovación');
    return access_token;
  }

  // console.log('[Token] Access token expirado. Verificando refresh token...');
  const { refreshToken } = getRefreshTokenContext();

  if (isTokenExpired(refreshToken)) {
    // console.log('[Token] Refresh token también expirado. Sesión finalizada');
    logout();
    throw new Error('Sesión expirada');
  }

  const isValidRefreshTokenID = await validateRefreshTokenID();
  if (!isValidRefreshTokenID) {
    console.log('[Token] ID de refresh token no válido. Posible inicio de sesión en otro dispositivo.');
    logout();
    throw new Error('Sesión cerrada por inicio de sesión en otro dispositivo');
  }

  // console.log('[Token] Refresh token válido. Solicitando nuevo access token...');
  access_token = await refreshAccessToken();
  // console.log('[Token] Nuevo access token recibido:', access_token?.substring(0, 15) + '...');
  return access_token;
}

  async function fetchWithAuth(url, options = {}) {
    //console.log(`[API] Solicitud a: ${url}`);
    const access_token = await verificarYRenovarToken();
    
    // console.log('[API] Enviando solicitud con token:', access_token?.substring(0, 15) + '...');
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${access_token}`
    };
    
    return fetch(url, { ...options, headers });
  }


function logout() {
  clearRefreshTokenValidationCache();
  refreshTokenValidationPromise = null;
  refreshAccessTokenPromise = null;
  jwtUtils.removeTokensFromCookie();
  window.location.href = '/'; // Redirigir a la página de login
}

export { fetchWithAuth, verificarYRenovarToken, logout, validateRefreshTokenID };